import { formatValidQueryParamUrl } from "utils/common";
import { formatStaffAvailability } from "utils/staff-availability.util";

import { emptySplitApi } from "./empty-split-api";
import type {
  StaffAvailabilityFilters,
  StaffAvailabilityGroup,
  StaffMemberAvailability,
  StaffMemberAvailabilityResponse
} from "./types/staff-availability.type";

export function groupByStaffMemberId(
  staffGroupedAvailability: StaffAvailabilityGroup,
  currentAvailability: StaffMemberAvailability
) {
  const currentGroup = staffGroupedAvailability[
    currentAvailability.staff_member?.id
  ] || { staffMember: currentAvailability.staff_member, availability: [] };
  currentGroup.availability.push({
    startTime: currentAvailability.start_moment,
    endTime: currentAvailability.end_moment,
    clinic: currentAvailability.clinic,
    block_id: currentAvailability.block_id,
    block_name: currentAvailability.block_name,
    block_color: currentAvailability.block_color
  });
  staffGroupedAvailability[currentAvailability.staff_member?.id] = currentGroup;
  return staffGroupedAvailability;
}

export const staffAvailabilityApi = emptySplitApi.injectEndpoints({
  endpoints: (builder) => ({
    getStaffAvailability: builder.query<
      StaffMemberAvailabilityResponse,
      StaffAvailabilityFilters
    >({
      query: ({
        staffMembersIds,
        currentClinicId,
        region,
        fromDate,
        roomId,
        slotDurationMinutes = 60,
        excludePersonalEvents = true,
        appointmentTypeId,
        limitToAppointmentBlocks,
        includeAppointmentBlockSlots,
        toDate = "",
        limit = 100,
        limitPerStaffMember,
        offset = 0
      }: StaffAvailabilityFilters) =>
        `/staff-availability/?${formatValidQueryParamUrl({
          staff_member: staffMembersIds?.join(","),
          clinic: currentClinicId,
          from_moment: fromDate,
          to_moment: toDate,
          slot_duration_minutes: slotDurationMinutes,
          room: roomId,
          region,
          exclude_personal_events: excludePersonalEvents,
          appointment_type: appointmentTypeId,
          limit_to_appointment_blocks:
            appointmentTypeId !== undefined
              ? !!limitToAppointmentBlocks
              : undefined,
          include_appointment_block_slots: includeAppointmentBlockSlots
            ? true
            : undefined,
          limit,
          limit_per_staff_member: limitPerStaffMember,
          offset
        })}&snap_to_minutes=15`,
      providesTags: (result, error, args) => {
        if (args.staffMembersIds?.length) {
          return args.staffMembersIds.map((id) => ({
            type: "StaffAvailability" as const,
            id: id
          }));
        }

        return (
          result?.map((staffAvailability) => ({
            type: "StaffAvailability" as const,
            id: staffAvailability.staff_member?.id
          })) ?? []
        );
      },
      transformResponse: (response: StaffMemberAvailabilityResponse) => {
        return response.map(formatStaffAvailability);
      }
    })
  })
});

export const {
  useGetStaffAvailabilityQuery,
  useLazyGetStaffAvailabilityQuery
} = staffAvailabilityApi;
